/* eslint-disable max-len */
import * as yup from 'yup';
// import { PHONE_REGEX } from '@src/constants/regex';
import dayjs from 'dayjs';
import { EIN_REGEX, SSN_REGEX } from '@src/constants/regex';
import { isValidDateString, isDateInRange } from '@src/common/helpers/dateHelpers';

export const FORM_CURRENT_STEP = 'current';
export const FORM_UPCOMING_STEP = 'upcoming';
export const FORM_COMPLETE_STEP = 'complete';

const personalDetailsBasicSchema = {
    title: yup.string().nullable(),
    first_name: yup.string().required('First name is required'),
    middle_name: yup.string().nullable(),
    last_name: yup.string().required('Last name is required'),
    DOB: yup.string()
        .required('Date of birth is required')
        .test('maxDate', 'Date Must be in the past', (date) => +new Date(`${date}T00:00:00`) <= +new Date())
        .test('minDate', 'Invalid date', (date) => new Date(`${date}T00:00:00`) >= new Date('1900-01-01T00:00:00')),
    address01: yup.string().required('Address is required'),
    address02: yup.string().nullable(),
    city: yup.string().required('City is required'),
    postal_code: yup.string().required('Postal Code is required'),
    state: yup.string().required('State is required'),
    mailing_address01: yup.string().nullable(),
    mailing_address02: yup.string().nullable(),
    mailing_city: yup.string().nullable(),
    mailing_postal_code: yup.string().nullable(),
    mailing_state: yup.string().nullable(),
    email: yup
        .string()
        .email('Please type valid email address')
        .required('Email is required'),
    phone: yup
        .string()
        .required('Phone is required'),
    // .matches(PHONE_REGEX, 'Phone number is not valid'),
    occupation: yup.string().required('Occupation is required'),
    control_group: yup.bool(),
    gender: yup.string()
        .required('Gender is required'),
    ssn_number: yup.string()
        .matches(SSN_REGEX[0], 'Should be valid SSN')
        .matches(SSN_REGEX[1], 'Should be valid SSN')
        .required(),
};

export const personalDetailsSettingsSchema = yup.object().shape({
    ...personalDetailsBasicSchema,
    spouse_first_name: yup.string().nullable(),
    spouse_last_name: yup.string().nullable(),
    spouse_email: yup.string().nullable().email('Please type valid email address'),
});

export const personalDetailsSchema = yup.object().shape({
    ...personalDetailsBasicSchema,
    married: yup.bool(),
    spouse_first_name: yup.string().when('married', {
        is: true,
        then: (schema) => schema.required('Spouse\'s First Name is required'),
    }),
    spouse_last_name: yup.string().when('married', {
        is: true,
        then: (schema) => schema.required('Spouse\'s Last Name is required'),
    }),
    spouse_email: yup.string().email('Please type valid email address').when('married', {
        is: true,
        then: (schema) => schema.required('Spouse\'s email is required'),
    }),
});

export const bankDetailsSchema = yup.object().shape({
    bank_terms_agree: yup
        .boolean()
        .required('The terms and conditions must be accepted.')
        .oneOf([true], 'The terms and conditions must be accepted.'),
});

export const employerDetailsSchema = yup.object().shape({
    employer_ein_number: yup.string()
        .matches(EIN_REGEX, 'Should be valid EIN')
        .required(),
    employer_name: yup.string().required('First name is required'),
    employer_address01: yup.string().required('Address is required'),
    employer_address02: yup.string(),
    employer_city: yup.string().required('City is required'),
    employer_postal_code: yup.string().required('Postal code is required'),
    employer_state: yup.string().required('State is required'),
    employer_email: yup.string().email('Please type valid email address'),
    employer_phone: yup
        .string(),
    // .matches(PHONE_REGEX, 'Phone number is not valid'),
    employer_website: yup.string(),
    employer_mailing_equals_billing: yup.boolean(),
    employer_mailing_address01: yup
        .string()
        .when('employer_mailing_equals_billing', {
            is: true,
            then: (schema) => schema.required('Mailing address is required'),
        }),
    employer_mailing_address02: yup.string(),
    employer_mailing_city: yup
        .string()
        .when('employer_mailing_equals_billing', {
            is: true,
            then: (schema) => schema.required('Mailing city is required'),
        }),
    employer_mailing_postal_code: yup
        .string()
        .when('employer_mailing_equals_billing', {
            is: true,
            then: (schema) => schema.required('Mailing Postal code is required'),
        }),
    employer_mailing_state: yup
        .string()
        .when('employer_mailing_equals_billing', {
            is: true,
            then: (schema) => schema.required('Mailing state is required'),
        }),
    entity_type: yup.string().required('Entity type is required'),
    employer_incorp_state: yup
        .string()
        .required('Incorporation State is required'),
    // Use string + custom validation instead of yup.date() to prevent timezone off-by-one shifts.
    employer_incorp_date: yup
        .string()
        .required('Incorporation date is required')
        .test('valid-date', 'Incorporation date is required', (value) => {
            if (!value) return true;
            return isValidDateString(value);
        })
        .test('date-min', 'Invalid date', (value) => {
            if (!value) return true;
            const minYear = new Date().getFullYear() - 100;
            return isDateInRange(value, { year: minYear, month: 1, day: 1 });
        })
        .test('date-max', 'Must be in the past', (value) => {
            if (!value) return true;
            const today = new Date();
            return isDateInRange(value, { year: 1900, month: 1, day: 1 }, {
                year: today.getFullYear(),
                month: today.getMonth() + 1,
                day: today.getDate(),
            });
        }),
    employer_fiscal_year_end: yup
        .string()
        .required('Tax year end date is required'),
    employer_fiscal_year_end_date: yup
        .string()
        .nullable()
        .when('employer_fiscal_year_end', {
            is: 'fiscalBased',
            then: (schema) => schema.required('Employer Fiscal Year End Date is required'),
        }),
    employer_naics_code: yup
        .string()
        .required('Industry code is required'),
});

export const planDetailsSchema = yup.object().shape({
    plan_loan: yup.boolean(),
    plan_roth: yup.boolean(),
    plan_roth_roll: yup.boolean(),
    plan_name: yup.string().required('Plan Name is required'),
    plan_establish_date: yup
        .date()
        .max(new Date(), 'Must be in the past')
        .min(dayjs().subtract(100, 'years').toDate(), 'Invalid date')
        .typeError('Tax Year Plan is Established is required')
        .required('Tax Year Plan is Established is required'),
    loan_interest_rate: yup
        .number()
        .min(1, 'Minimum loan interest rate is 1%')
        .max(100, 'Maximum loan interest rate is 100%')
        .typeError('Loan interest rate must be a number'),
    loan_interest_rate_type: yup.string(),
    plan_signer_first_name: yup.string().required('First Name is required'),
    plan_signer_last_name: yup.string().required('Last Name is required'),
    plan_signer_title: yup.string().required('Title is required'),
    isSelectedSignature: yup.boolean().oneOf([true, false]),
    plan_signature: yup.string().when('isSelectedSignature', {
        is: false,
        then: (schema) => schema
            .required('Signature is required')
            .matches(
                /^data:image\/(?:gif|png|jpeg|bmp|webp|svg\+xml)(?:;charset=utf-8)?;base64,(?:[A-Za-z0-9]|[+/])+={0,2}/,
                'Signature must be png',
            ),
    }),
    plan_sign_date: yup
        .date()
        .max(dayjs().add(1, 'day').toDate(), 'Must be in the past')
        .min(dayjs().subtract(100, 'years').toDate(), 'Invalid date')
        .typeError('Signed on Date is required')
        .required('Signed on Date is required'),
});

export const signerDetailsSchema = yup.object().shape({
    participant_signer_first_name: yup.string().required('First Name is required'),
    participant_signer_last_name: yup.string().required('Last Name is required'),
    participant_signer_title: yup.string().required('Title is required'),
    isSelectedSignature: yup.boolean().oneOf([true, false]),
    participant_signature: yup.string().when('isSelectedSignature', {
        is: false,
        then: (schema) => schema
            .required('Signature is required')
            .matches(
                /^data:image\/(?:gif|png|jpeg|bmp|webp|svg\+xml)(?:;charset=utf-8)?;base64,(?:[A-Za-z0-9]|[+/])+={0,2}/,
                'Signature must be png',
            ),
    }),
    participant_sign_date: yup
        .date()
        .max(dayjs().add(1, 'day').toDate(), 'Must be in the past')
        .min(dayjs().subtract(100, 'years').toDate(), 'Invalid date')
        .typeError('Signed on Date is required')
        .required('Signed on Date is required'),
});

export const planSettingsSchema = yup.object().shape({
    plan_ein_number: yup.string()
        .matches(EIN_REGEX, 'Should be valid EIN')
        .required(),
    plan_name: yup.string().required('Plan Name is required'),
    // Use string + custom validation instead of yup.date() to prevent timezone off-by-one shifts.
    plan_establish_date: yup
        .string()
        .required('Tax Year Plan is Established is required')
        .test('valid-date', 'Tax Year Plan is Established is required', (value) => {
            if (!value) return true;
            return isValidDateString(value);
        })
        .test('date-min', 'Invalid date', (value) => {
            if (!value) return true;
            const minYear = new Date().getFullYear() - 100;
            return isDateInRange(value, { year: minYear, month: 1, day: 1 });
        })
        .test('date-max', 'Must be in the past', (value) => {
            if (!value) return true;
            const today = new Date();
            return isDateInRange(value, { year: 1900, month: 1, day: 1 }, {
                year: today.getFullYear(),
                month: today.getMonth() + 1,
                day: today.getDate(),
            });
        }),
});

const ALLOWED_CHARACTERS = /^[0-9a-zA-Z\s]*$/;
export const DIGITS_ONLY = /^[0-9]*$/;

export const naicsSearchSchema = yup.object().shape({
    search_value: yup.string()
        .test(
            'min',
            'Search value must contain at least 2 non-whitespace characters',
            (value) => value.replaceAll(' ', '').length >= 2,
        )
        .test(
            'allowedCharacters',
            'Only letters digits and spaces are allowed',
            (value) => ALLOWED_CHARACTERS.test(value),
        )
        .test(
            'maxCodeLength',
            'Code length should not be longer then 6 digits',
            (value) => !(DIGITS_ONLY.test(value) && value.length > 6),
        ),
});

export const manualNaicsCodeSchema = yup.object().shape({
    manual_code: yup.string()
        .required('NAICS code is required')
        .matches(/^[0-9]{2,6}$/, 'Must be 2-6 digits'),
    manual_description: yup.string()
        .max(200, 'Description must be 200 characters or fewer'),
});

export const controlledEmployerSchema = yup.object().shape({
    employer_ein: yup.string()
        .matches(EIN_REGEX, 'Should be valid EIN')
        .required(),
    employer_name: yup.string().required('First name is required'),
    employer_address01: yup.string().required('Address is required'),
    employer_address02: yup.string(),
    employer_city: yup.string().required('City is required'),
    employer_postal_code: yup.string().required('Postal code is required'),
    employer_state: yup.string().required('State is required'),
    employer_email: yup.string().email('Please type valid email address'),
    employer_phone: yup
        .string(),
    // .matches(PHONE_REGEX, 'Phone number is not valid'),
    employer_website: yup.string(),
});
